import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Gavel,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertTriangle,
  FileText,
  Download,
  ChevronDown,
  ChevronRight,
  Scale,
  Users,
  Clock,
  Shield,
  Paperclip,
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CAUSE_OPTIONS = [
  'Breach of Agreement / Contract',
  'Failure to Respond to Lawful Notice',
  'Failure to Verify Claim Under Penalty of Perjury',
  'Unjust Enrichment',
  'Administrative Dishonor / Default',
  'Procedural Violations / Due Process',
  'Fraud / Misrepresentation',
  'Deprivation of Rights Under Color of Law',
  'Breach of Fiduciary Duty',
  'Violation of Magna Carta (1215) Ch. 39 & 40',
];

const RELIEF_OPTIONS = [
  'Dismissal of all claims against Plaintiff',
  'Monetary damages (compensatory)',
  'Declaratory relief / Judgment',
  'Permanent injunction',
  'Correction of records',
  'Discharge of alleged obligation',
  'Court costs and fees',
  'Any other relief the Court deems just and equitable',
];

const ComplaintPortal = ({ conversationId: propConversationId, embedded = false }) => {
  const params = useParams();
  const conversationId = propConversationId || params.conversationId;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    jurisdiction: true, parties: true, facts: false, causes: false,
    evidence: false, defaultNarrative: false, injury: false, relief: false,
  });

  // Form state
  const [courtType, setCourtType] = useState('United States District Court');
  const [courtLocation, setCourtLocation] = useState('');
  const [jurisdictionBasis, setJurisdictionBasis] = useState('Equity jurisdiction — the Plaintiff has no adequate remedy at law. The administrative record establishes default and dishonor by the Defendant.');
  const [statementOfFacts, setStatementOfFacts] = useState('');
  const [selectedCauses, setSelectedCauses] = useState([]);
  const [customCause, setCustomCause] = useState('');
  const [defaultNarrative, setDefaultNarrative] = useState('');
  const [injuryDescription, setInjuryDescription] = useState('');
  const [selectedRelief, setSelectedRelief] = useState([]);
  const [customRelief, setCustomRelief] = useState('');

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Fetch case data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/api/complaint-portal/${conversationId}/data`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.data.success) {
          const d = res.data.data;
          setCaseData(d);
          setCourtLocation(`District of ${d.client_info?.state || ''}`);

          // Restore saved complaint document if it exists
          try {
            const convRes = await axios.get(`${API_BASE}/api/conversations/${conversationId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (convRes.data?.metadata?.generated_complaint_doc?.content) {
              setGeneratedDoc(convRes.data.metadata.generated_complaint_doc);
            }
          } catch (e) { console.error('Failed to check saved complaint:', e); }
          // Auto-build statement of facts from timeline and mailings
          let facts = '';
          if (d.strategy_timeline?.length) {
            facts += d.strategy_timeline.map((t, i) => `${i + 1}. On ${t.date || 'date unknown'}, ${t.event}`).join('\n');
          }
          if (d.mailings?.length) {
            facts += '\n\n' + d.mailings.map((m, i) =>
              `${(d.strategy_timeline?.length || 0) + i + 1}. ${m.document_name || 'Notice'} was sent to ${m.recipient || 'Defendant'} via certified mail (Tracking: ${m.tracking_number || 'N/A'}). ${m.delivered_at ? `Delivered on ${m.delivered_at}.` : 'Delivery pending.'} ${m.response_received ? 'Response was received.' : 'No response was received within the response window.'}`
            ).join('\n');
          }
          if (!facts) facts = 'The Plaintiff initiated the administrative process by sending lawful notices to the Defendant via certified mail, return receipt requested. Despite proper service and sufficient time to respond, the Defendant failed to provide a verified response.';
          setStatementOfFacts(facts);

          // Auto-build default narrative
          const noResponses = (d.mailings || []).filter(m => !m.response_received);
          let narrative = `Proper notice was given to ${d.opposing_party_info?.fullName || 'the Defendant'} on all counts. `;
          if (noResponses.length > 0) {
            narrative += `${noResponses.length} notice(s) were sent and remained unanswered. `;
          }
          if (d.has_opportunity_cure) {
            narrative += 'An Opportunity to Cure was provided, granting an additional 10 days to respond with verified evidence. ';
          }
          narrative += 'The Defendant failed to provide any verified proof of claim, original contract, or lawful authority. Under the maxims of Equity Jurisprudence, silence constitutes acquiescence and agreement to all terms set forth in the original notices. The administrative default is now established and binding.';
          setDefaultNarrative(narrative);

          // Pre-select common causes
          setSelectedCauses(['Failure to Respond to Lawful Notice', 'Failure to Verify Claim Under Penalty of Perjury', 'Administrative Dishonor / Default']);
          setSelectedRelief(['Declaratory relief / Judgment', 'Dismissal of all claims against Plaintiff', 'Any other relief the Court deems just and equitable']);
        }
      } catch (err) {
        console.error('Failed to load complaint data:', err);
        toast({ title: 'Error', description: 'Failed to load case data.', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    if (conversationId) fetchData();
  }, [conversationId, toast]);

  const handleGenerate = async () => {
    if (!caseData) return;
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');

      const exhibits = (caseData.evidence || []).map((ev, i) => ({
        label: `Exhibit ${String.fromCharCode(65 + i)}`,
        filename: ev.filename,
        description: ev.description || ev.document_type,
        type: ev.document_type,
      }));
      // Add mailing receipts as exhibits
      (caseData.mailings || []).forEach((m, i) => {
        if (m.tracking_number) {
          exhibits.push({
            label: `Exhibit ${String.fromCharCode(65 + (caseData.evidence?.length || 0) + i)}`,
            filename: `Certified Mail Receipt - ${m.document_name || 'Notice'}`,
            description: `Tracking: ${m.tracking_number}, Status: ${m.delivery_status || 'sent'}`,
            type: 'mailing_receipt',
          });
        }
      });

      const payload = {
        client_info: caseData.client_info,
        opposing_party_info: caseData.opposing_party_info,
        category: caseData.category,
        case_description: caseData.case_description,
        court_type: courtType,
        court_location: courtLocation,
        jurisdiction_basis: jurisdictionBasis,
        statement_of_facts: statementOfFacts,
        causes_of_action: [...selectedCauses, ...(customCause ? [customCause] : [])],
        default_narrative: defaultNarrative,
        injury_description: injuryDescription,
        prayer_for_relief: [...selectedRelief, ...(customRelief ? [customRelief] : [])],
        exhibits,
        admin_history: caseData.admin_process_status,
      };

      const res = await axios.post(
        `${API_BASE}/api/complaint-portal/${conversationId}/generate`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (res.data.success && res.data.job_id) {
        const jobId = res.data.job_id;
        toast({ title: 'Generating Complaint', description: 'AI is drafting your Bill of Complaint. This may take 30-60 seconds...' });

        let attempts = 0;
        while (attempts < 60) {
          attempts++;
          await new Promise(r => setTimeout(r, 3000));
          try {
            const pollRes = await axios.get(
              `${API_BASE}/api/conversations/${conversationId}/document-job/${jobId}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (pollRes.data.status === 'completed' && pollRes.data.document) {
              setGeneratedDoc(pollRes.data.document);
              // Save to conversation metadata so it persists
              try {
                const convRes = await axios.get(`${API_BASE}/api/conversations/${conversationId}`, { headers: { 'Authorization': `Bearer ${token}` } });
                const existingMeta = convRes.data?.metadata || {};
                existingMeta.generated_complaint_doc = pollRes.data.document;
                await axios.put(`${API_BASE}/api/conversations/${conversationId}`, { metadata: existingMeta }, { headers: { 'Authorization': `Bearer ${token}` } });
              } catch (saveErr) { console.error('Failed to save complaint doc:', saveErr); }
              toast({ title: 'Complaint Generated', description: 'Your Bill of Complaint is ready for download.' });
              setGenerating(false);
              return;
            } else if (pollRes.data.status === 'failed') {
              const errMsg = pollRes.data.error || 'Generation failed';
              if (errMsg.toLowerCase().includes('budget') || errMsg.toLowerCase().includes('max budget') || errMsg.includes('exceeded')) {
                throw new Error('Your AI key balance has been exceeded. Please go to Profile → Universal Key → Add Balance to continue generating documents.');
              }
              throw new Error(errMsg);
            }
          } catch (pollErr) {
            if (pollErr.message) throw pollErr;
          }
        }
        throw new Error('Generation timed out. Please try again.');
      }
    } catch (err) {
      toast({ title: 'Generation Failed', description: err.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedDoc?.content) return;
    try {
      const byteChars = atob(generatedDoc.content);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: generatedDoc.mime_type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generatedDoc.filename || 'Bill of Complaint.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Download Failed', description: 'Could not download the document.', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-8 h-8 mx-auto text-red-500 mb-2" />
        <p className="text-gray-600">Failed to load case data.</p>
        <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  const SectionHeader = ({ sectionKey, title, icon: Icon }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
      data-testid={`section-${sectionKey}`}
    >
      <span className="flex items-center gap-2 font-semibold text-sm text-gray-800">
        <Icon className="w-4 h-4" />
        {title}
      </span>
      {expandedSections[sectionKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    </button>
  );

  return (
    <div className={`${embedded ? '' : 'max-w-4xl mx-auto'} p-3 sm:p-4 space-y-4`} data-testid="complaint-portal">
      {/* Header */}
      {!embedded && (
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/dashboard/chat/${conversationId}`)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Case
          </Button>
        </div>
      )}

      <div className="space-y-1">
        <h1 className={`${embedded ? 'text-lg sm:text-xl' : 'text-2xl'} font-bold text-gray-900 flex items-center gap-2`}>
          <Gavel className="w-5 h-5 sm:w-6 sm:h-6 text-blue-700" />
          Bill of Complaint Portal
        </h1>
        <p className="text-xs sm:text-sm text-gray-600">
          Generate a court-ready Verified Complaint for Equitable Relief based on your completed administrative record.
        </p>
      </div>

      {/* Admin Process Status */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-gray-800 mb-2">Administrative Record Summary</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-2 bg-blue-50 rounded-lg">
              <p className="text-lg font-bold text-blue-700">{caseData.admin_process_status?.notices_sent || 0}</p>
              <p className="text-[10px] text-gray-600">Notices Sent</p>
            </div>
            <div className="text-center p-2 bg-green-50 rounded-lg">
              <p className="text-lg font-bold text-green-700">{caseData.admin_process_status?.documents_generated || 0}</p>
              <p className="text-[10px] text-gray-600">Docs Generated</p>
            </div>
            <div className="text-center p-2 bg-purple-50 rounded-lg">
              <p className="text-lg font-bold text-purple-700">{caseData.admin_process_status?.evidence_attached || 0}</p>
              <p className="text-[10px] text-gray-600">Evidence Files</p>
            </div>
            <div className="text-center p-2 bg-amber-50 rounded-lg">
              <p className="text-lg font-bold text-amber-700">{caseData.admin_process_status?.has_default ? 'Yes' : 'Pending'}</p>
              <p className="text-[10px] text-gray-600">Default Established</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Structured Complaint Builder */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Complaint Builder</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">

          {/* A. Jurisdiction */}
          <SectionHeader sectionKey="jurisdiction" title="A. Jurisdiction & Venue" icon={Scale} />
          {expandedSections.jurisdiction && (
            <div className="space-y-2 pl-2 pb-2">
              <div>
                <label className="text-xs font-medium text-gray-600">Court Type</label>
                <Input value={courtType} onChange={e => setCourtType(e.target.value)} className="text-sm" data-testid="court-type" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Court Location / District</label>
                <Input value={courtLocation} onChange={e => setCourtLocation(e.target.value)} className="text-sm" data-testid="court-location" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Basis for Jurisdiction</label>
                <Textarea value={jurisdictionBasis} onChange={e => setJurisdictionBasis(e.target.value)} className="text-sm min-h-[60px]" data-testid="jurisdiction-basis" />
              </div>
            </div>
          )}

          {/* B. Parties */}
          <SectionHeader sectionKey="parties" title="B. Parties" icon={Users} />
          {expandedSections.parties && (
            <div className="space-y-2 pl-2 pb-2">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs font-bold text-blue-800">Plaintiff (You)</p>
                <p className="text-sm text-blue-900 font-medium">{caseData.client_info?.fullName || 'Not set'}</p>
                <p className="text-xs text-blue-700">{caseData.client_info?.streetAddress}, {caseData.client_info?.city}, {caseData.client_info?.state} {caseData.client_info?.zipCode}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-xs font-bold text-red-800">Defendant</p>
                <p className="text-sm text-red-900 font-medium">{caseData.opposing_party_info?.fullName || 'Not set'}</p>
                <p className="text-xs text-red-700">{caseData.opposing_party_info?.fullAddress || ''}</p>
              </div>
            </div>
          )}

          {/* C. Statement of Facts */}
          <SectionHeader sectionKey="facts" title="C. Statement of Facts" icon={Clock} />
          {expandedSections.facts && (
            <div className="pl-2 pb-2">
              <p className="text-xs text-gray-500 mb-1">Auto-populated from your timeline and mailing records. Edit as needed.</p>
              <Textarea value={statementOfFacts} onChange={e => setStatementOfFacts(e.target.value)} className="text-sm min-h-[140px]" data-testid="statement-of-facts" />
            </div>
          )}

          {/* D. Causes of Action */}
          <SectionHeader sectionKey="causes" title="D. Causes of Action" icon={AlertTriangle} />
          {expandedSections.causes && (
            <div className="space-y-2 pl-2 pb-2">
              <p className="text-xs text-gray-500">Select all that apply:</p>
              <div className="grid grid-cols-1 gap-1">
                {CAUSE_OPTIONS.map(cause => (
                  <label key={cause} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedCauses.includes(cause)}
                      onChange={e => {
                        if (e.target.checked) setSelectedCauses(prev => [...prev, cause]);
                        else setSelectedCauses(prev => prev.filter(c => c !== cause));
                      }}
                      className="rounded"
                    />
                    {cause}
                  </label>
                ))}
              </div>
              <Input value={customCause} onChange={e => setCustomCause(e.target.value)} placeholder="Add custom cause of action..." className="text-sm" />
            </div>
          )}

          {/* E. Evidence / Exhibits */}
          <SectionHeader sectionKey="evidence" title={`E. Evidence & Exhibits (${(caseData.evidence?.length || 0) + (caseData.mailings?.length || 0)} items)`} icon={Paperclip} />
          {expandedSections.evidence && (
            <div className="space-y-1.5 pl-2 pb-2">
              <p className="text-xs text-gray-500">These will be referenced as Exhibits in the complaint:</p>
              {(caseData.evidence || []).map((ev, i) => (
                <div key={ev.evidence_id} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-1.5">
                  <Badge variant="outline" className="text-[10px] font-mono">Ex. {String.fromCharCode(65 + i)}</Badge>
                  <FileText className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs text-gray-700 flex-1">{ev.filename} — {ev.description || ev.document_type}</span>
                </div>
              ))}
              {(caseData.mailings || []).filter(m => m.tracking_number).map((m, i) => (
                <div key={`mail-${i}`} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-1.5">
                  <Badge variant="outline" className="text-[10px] font-mono">Ex. {String.fromCharCode(65 + (caseData.evidence?.length || 0) + i)}</Badge>
                  <FileText className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs text-gray-700 flex-1">{m.document_name || 'Certified Mail'} — Tracking: {m.tracking_number}</span>
                </div>
              ))}
              {!(caseData.evidence?.length || caseData.mailings?.length) && (
                <p className="text-xs text-amber-600">No evidence or mailing records found. Upload evidence in the Strategy Brain tab.</p>
              )}
            </div>
          )}

          {/* F. Default Narrative */}
          <SectionHeader sectionKey="defaultNarrative" title="F. Default / Non-Response Narrative" icon={Shield} />
          {expandedSections.defaultNarrative && (
            <div className="pl-2 pb-2">
              <p className="text-xs text-gray-500 mb-1">Auto-generated from your administrative record. This is the critical section. Edit as needed.</p>
              <Textarea value={defaultNarrative} onChange={e => setDefaultNarrative(e.target.value)} className="text-sm min-h-[100px]" data-testid="default-narrative" />
            </div>
          )}

          {/* G. Injury / Harm */}
          <SectionHeader sectionKey="injury" title="G. Injury / Harm" icon={AlertTriangle} />
          {expandedSections.injury && (
            <div className="pl-2 pb-2">
              <p className="text-xs text-gray-500 mb-1">Describe any harm suffered (financial, procedural, reputational, loss of time, etc.):</p>
              <Textarea value={injuryDescription} onChange={e => setInjuryDescription(e.target.value)} placeholder="Describe the harm or damages..." className="text-sm min-h-[80px]" data-testid="injury-description" />
            </div>
          )}

          {/* H. Prayer for Relief */}
          <SectionHeader sectionKey="relief" title="H. Prayer for Relief" icon={Gavel} />
          {expandedSections.relief && (
            <div className="space-y-2 pl-2 pb-2">
              <p className="text-xs text-gray-500">Select the relief you are seeking:</p>
              <div className="grid grid-cols-1 gap-1">
                {RELIEF_OPTIONS.map(relief => (
                  <label key={relief} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedRelief.includes(relief)}
                      onChange={e => {
                        if (e.target.checked) setSelectedRelief(prev => [...prev, relief]);
                        else setSelectedRelief(prev => prev.filter(r => r !== relief));
                      }}
                      className="rounded"
                    />
                    {relief}
                  </label>
                ))}
              </div>
              <Input value={customRelief} onChange={e => setCustomRelief(e.target.value)} placeholder="Add custom relief..." className="text-sm" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate / Download */}
      <Card>
        <CardContent className="p-4">
          {!generatedDoc ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-gray-600">
                Review all sections above, then generate your Bill of Complaint.
              </p>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="px-8"
                data-testid="generate-complaint-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating Complaint...</>
                ) : (
                  <><Gavel className="w-4 h-4 mr-2" />Generate Bill of Complaint</>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <p className="font-semibold text-sm">Bill of Complaint Generated Successfully</p>
              </div>
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium">{generatedDoc.filename}</p>
                    <p className="text-xs text-gray-500">Word Document (.docx)</p>
                  </div>
                </div>
                <Button size="sm" onClick={handleDownload} data-testid="download-complaint-btn">
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              </div>
              <div className="text-center">
                <Button variant="outline" size="sm" onClick={() => setGeneratedDoc(null)} data-testid="regenerate-complaint-btn">
                  Regenerate Complaint
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplaintPortal;
